import { useState } from "react";
import { GATE_BUCKETS, type GateBucket, type GateResult } from "@iamu/shared";
import { Link } from "react-router-dom";
import { Ban, Eye, RotateCcw, Link2 } from "lucide-react";
import { toast } from "sonner";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import {
  CreateIssueDialog,
  LinkExistingDialog,
} from "@/components/workflow/create-issue-dialog";
import { BulkCreateIssuesDialog } from "@/components/workflow/bulk-create-issues-dialog";
import { ProviderIcon } from "@/components/workflow/workflow-rail";
import { useOptionalProject } from "@/components/project-context";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { FindingRow } from "@/components/experience/finding-row";
import { TriageActionDialog } from "@/components/experience/triage-action-dialog";
import type { FindingType, Severity } from "@/components/experience/vocabulary";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  BUCKET_EXPLAINERS,
  BUCKET_LABELS,
  REASON_LABELS,
} from "./gate-vocabulary";
import type { ScenarioLabel } from "./gate-coverage";

type Item = GateResult["items"][number];

function bucketCount(result: GateResult, bucket: GateBucket): number {
  if (bucket === "strength_lost") return result.counts.strengths_lost;
  const c = result.counts[bucket];
  return c.critical + c.major + c.minor;
}

function ReopenFindingDialog({
  finding,
  projectId,
  onClose,
  onSuccess,
}: {
  finding: { canonical_id: string; title: string; walkthrough_id: string };
  projectId: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [note, setNote] = useState("");
  const overrideMutation = trpc.finding.overrideLedgerOutcome.useMutation({
    onSuccess: () => {
      toast.success("Finding reopened");
      onSuccess();
      onClose();
    },
    onError: (err) => {
      toastTrpcError(err, "Failed to reopen finding");
    },
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Mark as not fixed</DialogTitle>
          <DialogDescription>
            Overturn the resolved outcome for this finding. This records a human
            override in the ledger, transitions the finding back to Open, and
            allows re-evaluating the quality gate.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <p className="text-sm font-medium text-foreground truncate">
            {finding.title}
          </p>
          <div className="space-y-1.5">
            <Label htmlFor="reopen-note">Reason or context (optional)</Label>
            <Textarea
              id="reopen-note"
              placeholder="e.g. Bug still reproduces on checkout"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
            />
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={overrideMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={overrideMutation.isPending}
            onClick={() => {
              overrideMutation.mutate({
                projectId,
                walkthroughId: finding.walkthrough_id,
                canonicalFindingId: finding.canonical_id,
                note: note.trim() || undefined,
              });
            }}
          >
            {overrideMutation.isPending ? "Reopening..." : "Reopen finding"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** The bucket-specific line under a finding's title. */
function evidenceLine(item: Item): string {
  switch (item.bucket) {
    case "fixed":
      return item.evidence.step_id
        ? "Re-run reported it resolved, with the step as evidence."
        : "Re-run reported it resolved.";
    case "not_rechecked":
      return item.reason
        ? `${REASON_LABELS[item.reason]}.`
        : "Not re-checked in this build.";
    case "accepted": {
      const how =
        item.accepted?.state === "risk_accepted"
          ? "Risk accepted"
          : "Dismissed";
      const why = item.accepted?.reason?.replace(/_/g, " ");
      return [how, why, item.accepted?.note].filter(Boolean).join(": ");
    }
    case "new":
      return item.no_baseline
        ? "First run of this scenario: listed, but cannot fail the gate."
        : `Seen in ${item.evidence.seen ?? "1/1"} runs of this build.`;
    case "strength_lost":
      return "Seen at the baseline, not seen again in this build.";
    default:
      return item.evidence.seen
        ? `Seen in ${item.evidence.seen} runs of this build.`
        : "";
  }
}

/**
 * Every in-scope problem sorted into its bucket, one tab per bucket
 * (count in the label). Rows reuse the findings queue row; the trailing
 * column links to the run that is the evidence and provides inline actions.
 */
export function GateBuckets({
  result,
  projectId,
  buildId: _buildId,
  scenarios,
  findingDescriptions,
  canEdit = false,
  onReevaluate,
}: {
  result: GateResult;
  projectId: string;
  buildId?: string;
  scenarios: ScenarioLabel[];
  findingDescriptions?: Record<string, string>;
  canEdit?: boolean;
  onReevaluate?: () => void;
}) {
  const utils = trpc.useUtils();
  const project = useOptionalProject();
  const { data: trackerTarget } = trpc.workflow.integrationTarget.get.useQuery(
    { projectId },
    { staleTime: 60_000 },
  );
  const hasLinkedRepo = Boolean(project?.githubRepositoryId);
  const activeProvider =
    trackerTarget?.provider ?? (hasLinkedRepo ? "github" : null);
  const isTrackerConfigured = Boolean(
    trackerTarget?.targetKey || hasLinkedRepo,
  );

  const [createIssueCanonicalId, setCreateIssueCanonicalId] = useState<
    string | null
  >(null);
  const [linkExistingCanonicalId, setLinkExistingCanonicalId] = useState<
    string | null
  >(null);
  const [bulkCanonicalIds, setBulkCanonicalIds] = useState<string[] | null>(
    null,
  );

  const [acceptingItem, setAcceptingItem] = useState<Item | null>(null);
  const [reopeningItem, setReopeningItem] = useState<{
    canonical_id: string;
    title: string;
    walkthrough_id: string;
  } | null>(null);
  const labelOf = new Map(scenarios.map((s) => [s.id, s]));
  const activeBuckets = GATE_BUCKETS.filter((b) => bucketCount(result, b) > 0);
  const displayedBuckets =
    activeBuckets.length > 0 ? activeBuckets : GATE_BUCKETS.slice(0, 3);
  const first = displayedBuckets[0] ?? "new";
  return (
    <>
      <Tabs defaultValue={first}>
        <TabsList
          variant="line"
          aria-label="Finding buckets"
          className="max-w-full overflow-x-auto no-scrollbar justify-start group-data-[orientation=horizontal]/tabs:h-auto"
        >
          {displayedBuckets.map((bucket) => (
            <TabsTrigger key={bucket} value={bucket} className="flex-none">
              {BUCKET_LABELS[bucket]}
              <span className="tabular-nums text-muted-foreground">
                {bucketCount(result, bucket)}
              </span>
            </TabsTrigger>
          ))}
        </TabsList>
        {displayedBuckets.map((bucket) => {
          const items = result.items.filter((i) => i.bucket === bucket);
          return (
            <TabsContent key={bucket} value={bucket} className="space-y-3 pt-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm text-muted-foreground">
                  {BUCKET_EXPLAINERS[bucket]}
                </p>
                {canEdit &&
                  isTrackerConfigured &&
                  (bucket === "regressed" ||
                    bucket === "new" ||
                    bucket === "still_open" ||
                    bucket === "fix_didnt_hold") &&
                  items.length > 0 && (
                    <Button
                      variant="outline"
                      size="xs"
                      onClick={() =>
                        setBulkCanonicalIds(items.map((i) => i.canonical_id))
                      }
                    >
                      <ProviderIcon
                        provider={activeProvider || undefined}
                        className="mr-1.5 size-3"
                      />
                      Create issues for all {items.length}{" "}
                      {BUCKET_LABELS[bucket].toLowerCase()}…
                    </Button>
                  )}
              </div>
              {items.length === 0 ? (
                <p className="text-sm text-muted-foreground">None.</p>
              ) : (
                <ul className="divide-y divide-border/60">
                  {items.map((item) => {
                    const scenario = item.scenario_id
                      ? labelOf.get(item.scenario_id)
                      : undefined;
                    const note = evidenceLine(item);
                    const preview =
                      findingDescriptions?.[item.canonical_id] ?? "";
                    return (
                      <li key={item.canonical_id} className="flex gap-3 py-3">
                        <FindingRow
                          href={`/projects/${projectId}/findings/${item.canonical_id}`}
                          finding={{
                            id: item.canonical_id,
                            title: item.title,
                            description: preview,
                            type: item.type as FindingType,
                            severity: (item.severity ?? "nit") as Severity,
                            personaName: scenario?.persona ?? null,
                            scenarioName: scenario?.situation ?? null,
                          }}
                          meta={
                            note ? (
                              <span className="text-muted-foreground">
                                {note}
                              </span>
                            ) : undefined
                          }
                          actions={
                            <>
                              {canEdit &&
                                isTrackerConfigured &&
                                bucket !== "fixed" &&
                                bucket !== "strength_lost" && (
                                  <>
                                    <Button
                                      variant="outline"
                                      size="xs"
                                      className="h-7 px-2 text-xs"
                                      onClick={() =>
                                        setCreateIssueCanonicalId(
                                          item.canonical_id,
                                        )
                                      }
                                    >
                                      <ProviderIcon
                                        provider={activeProvider || undefined}
                                        className="mr-1 size-3"
                                      />
                                      Create issue…
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="xs"
                                      className="h-7 px-2 text-xs"
                                      onClick={() =>
                                        setLinkExistingCanonicalId(
                                          item.canonical_id,
                                        )
                                      }
                                    >
                                      <Link2 className="mr-1 size-3" />
                                      Link…
                                    </Button>
                                  </>
                                )}
                              {item.evidence.walkthrough_id && (
                                <Button
                                  asChild
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                                >
                                  <Link
                                    to={`/projects/${projectId}/walkthroughs/${item.evidence.walkthrough_id}`}
                                    aria-label={`Open the evidence run for: ${item.title}`}
                                  >
                                    <Eye className="mr-1 h-3 w-3" />
                                    View run
                                  </Link>
                                </Button>
                              )}
                              {canEdit &&
                                bucket === "fixed" &&
                                item.evidence.walkthrough_id && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 px-2 text-xs text-destructive hover:bg-destructive/10 hover:text-destructive"
                                    onClick={() =>
                                      setReopeningItem({
                                        canonical_id: item.canonical_id,
                                        title: item.title,
                                        walkthrough_id:
                                          item.evidence.walkthrough_id!,
                                      })
                                    }
                                  >
                                    <RotateCcw className="mr-1 h-3 w-3" />
                                    Not fixed: reopen
                                  </Button>
                                )}
                              {canEdit &&
                                bucket !== "fixed" &&
                                bucket !== "accepted" &&
                                bucket !== "strength_lost" && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-7 px-2 text-xs"
                                    onClick={() => setAcceptingItem(item)}
                                  >
                                    <Ban className="mr-1 h-3 w-3" />
                                    Dismiss
                                  </Button>
                                )}
                            </>
                          }
                        />
                      </li>
                    );
                  })}
                </ul>
              )}
              {result.items_truncated && (
                <p className="text-sm text-muted-foreground">
                  The result lists the first 500 findings; counts are complete.
                </p>
              )}
            </TabsContent>
          );
        })}
      </Tabs>

      {acceptingItem && (
        <TriageActionDialog
          action="dismiss"
          ids={[acceptingItem.canonical_id]}
          projectId={projectId}
          subject={acceptingItem.title}
          onClose={() => setAcceptingItem(null)}
          onDone={() => {
            setAcceptingItem(null);
            void utils.build.get.invalidate();
            void utils.build.list.invalidate();
            void utils.gate.history.invalidate();
            void utils.finding.queueCounts.invalidate();
            onReevaluate?.();
          }}
        />
      )}

      {reopeningItem && (
        <ReopenFindingDialog
          finding={reopeningItem}
          projectId={projectId}
          onClose={() => setReopeningItem(null)}
          onSuccess={() => {
            void utils.build.get.invalidate();
            void utils.build.list.invalidate();
            void utils.gate.history.invalidate();
            void utils.finding.queueCounts.invalidate();
            onReevaluate?.();
          }}
        />
      )}
      {createIssueCanonicalId && (
        <CreateIssueDialog
          canonicalId={createIssueCanonicalId}
          open={!!createIssueCanonicalId}
          onOpenChange={(open) => !open && setCreateIssueCanonicalId(null)}
        />
      )}

      {linkExistingCanonicalId && (
        <LinkExistingDialog
          canonicalId={linkExistingCanonicalId}
          open={!!linkExistingCanonicalId}
          onOpenChange={(open) => !open && setLinkExistingCanonicalId(null)}
        />
      )}

      {bulkCanonicalIds && (
        <BulkCreateIssuesDialog
          projectId={projectId}
          canonicalIds={bulkCanonicalIds}
          open={!!bulkCanonicalIds}
          onOpenChange={(open) => !open && setBulkCanonicalIds(null)}
        />
      )}
    </>
  );
}
